import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ClockIcon, InstagramIcon, LinkIcon, SearchIcon } from "@signet/ui";
import { api, CreatorAvatar, desktop, SignetMark, useMe, type NowRange } from "@signet/web-kit";
import { webModules } from "../../../../modules/registry.web.ts";
import { placeIcon, quickPlaces } from "../shell/places.ts";
import { CreatorDetail } from "./creator-detail.tsx";
import { readRecents, rememberRecent } from "./recents.ts";
import { findLinkByUrl, parseClipboard, searchQuick, type ClipboardHint, type QuickCreator, type QuickRecent, type QuickResult } from "./search.ts";
import "./quick.css";

/* ──────────────────────────────────────────────────────────────────────────
   The desktop quick bar: Option+S from anywhere (Alt+S on Windows), like
   Raycast's launcher (Jay, 2026-10-01).

   The desktop shell shows this page in a small frosted window of its own
   (apps/desktop/main.mjs) and hides it again when it loses focus. Choosing a
   row sends its address to the main window and brings that forward; Enter
   with nothing typed only brings Signet forward.

   Today's Visitors, Clicks and Revenue sit across the top, drawn by the same
   cards as Now so the figures always agree. Below them, the places and
   creators opened lately, and an offer for whatever is on the clipboard.
   Typing lists every match: tabs, pages and creators. A creator's name and a
   space lists her pages ("lexi links", "lexi uploads").

   Typing and clicking are the whole interface: no shortcuts beyond the
   arrows, Enter and Escape (Jay: "no more keybinds other than option + s").
   ────────────────────────────────────────────────────────────────────────── */

// Jay's three, in his order. Active creators stays on Now.
const STATS = ["links.visitors", "links.clicks", "reporting.revenue"];
const TODAY: NowRange = { key: "today" };

type Roster = { creators: { name: string; photo: string | null; departedOn: string | null }[] };
type LinkIndex = Parameters<typeof findLinkByUrl>[1];

type Row = {
  key: string;
  label: ReactNode;
  detail?: string;
  type: string;
  icon: ReactNode;
  /** Where the row goes, looked up if it must be; null brings Signet forward. */
  target: () => Promise<string | null> | string | null;
  /** What to keep in recents once opened. */
  recent?: (to: string) => QuickRecent;
  /** The creator the row is about, whose figures open beside the list. */
  creator?: string;
};

/** Hands the address to the main window, or, opened in a browser, goes there. */
async function openInSignet(to: string | null) {
  if (desktop?.quickOpen) {
    await desktop.quickOpen(to);
    return;
  }
  window.location.assign(to ?? "/");
}

export function QuickBar() {
  const { me, can, error } = useMe();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);
  const [recents, setRecents] = useState<QuickRecent[]>(readRecents);
  const [clip, setClip] = useState<ClipboardHint | null>(null);
  const [opening, setOpening] = useState(false);

  // The window behind this page is the glass; the app's ink background would cover
  // it. The shell says which glass (/quick?glass=liquid for macOS 26's Liquid Glass, 1
  // for the older blur); a browser tab, for testing, gets a frosted panel of CSS.
  useEffect(() => {
    const root = document.documentElement;
    const glass = new URLSearchParams(location.search).get("glass");
    root.classList.add("signet-qb-page");
    root.dataset.qbMaterial = glass === "liquid" ? "liquid" : glass === "1" ? "blur" : "browser";
    // A shell that sizes the window to the page; older shells keep a fixed window.
    root.classList.toggle("signet-qb-fit", Boolean(desktop?.quickResize));
    return () => {
      root.classList.remove("signet-qb-page", "signet-qb-fit");
      delete root.dataset.qbMaterial;
    };
  }, []);

  // The window is as tall as the panel. A ResizeObserver, not a timer: it reports only
  // when the height really changes, and only the changes are sent.
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = panel.current;
    if (!el || !desktop?.quickResize) return;
    let last = 0;
    const send = () => {
      const height = Math.ceil(el.getBoundingClientRect().height);
      if (height === last) return;
      last = height;
      desktop?.quickResize?.(height).catch(() => {});
    };
    const observer = new ResizeObserver(send);
    observer.observe(el);
    send();
    return () => observer.disconnect();
  }, []);

  const readClipboard = useCallback(() => {
    desktop?.clipboardText?.().then((text) => setClip(parseClipboard(text ?? ""))).catch(() => setClip(null));
  }, []);

  // Each time the bar is shown it starts empty, with fresh figures, recents
  // and clipboard, as if it had just been opened.
  const reset = useCallback(() => {
    setQuery("");
    setActive(-1);
    setOpening(false);
    setRecents(readRecents());
    readClipboard();
    void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[1] === "now-stats" });
    requestAnimationFrame(() => input.current?.focus());
  }, [queryClient, readClipboard]);
  useEffect(() => {
    readClipboard();
    return desktop?.onQuickShown?.(reset);
  }, [reset, readClipboard]);

  const close = () => void desktop?.quickClose?.();

  const roster = useQuery({
    queryKey: ["quick", "roster"],
    queryFn: () => api<Roster>("/api/m/creators/roster"),
    enabled: Boolean(me) && can("creators.roster:view"),
    staleTime: 5 * 60_000,
  });
  const creators: QuickCreator[] = useMemo(
    () => (roster.data?.creators ?? []).map((c) => ({ name: c.name, photo: c.photo, departed: Boolean(c.departedOn) })),
    [roster.data],
  );
  const photoOf = (name?: string) => creators.find((c) => c.name.toUpperCase() === (name ?? "").toUpperCase())?.photo ?? null;

  const places = useMemo(() => (me ? quickPlaces(can) : []), [me]);
  const creatorPages = useMemo(
    () => (me ? webModules.flatMap((m) => m.creatorPages ?? []).filter((p) => can(p.needs)).sort((a, b) => a.order - b.order) : []),
    [me],
  );
  const stats = useMemo(
    () => (me ? webModules.flatMap((m) => m.nowStats ?? []).filter((s) => STATS.includes(s.id) && can(s.needs)).sort((a, b) => STATS.indexOf(a.id) - STATS.indexOf(b.id)) : []),
    [me],
  );

  // An Instagram address or one of our links, from the clipboard when nothing is
  // typed, or from the box itself: Jay pasted https://www.instagram.com/belmallk
  // into it on 2026-10-01 and got nothing, because only the clipboard was read.
  const typedHint = useMemo(() => parseClipboard(query), [query]);
  const hint = query.trim() ? typedHint : clip;

  // A web address is only worth a row if it is one of our links.
  const linkIndex = useQuery({
    queryKey: ["quick", "links-index"],
    queryFn: () => api<LinkIndex>("/api/m/links/workspace"),
    enabled: hint?.kind === "url" && can("links.link:view"),
    staleTime: 60_000,
  });
  const foundLink = hint?.kind === "url" && linkIndex.data ? findLinkByUrl(hint, linkIndex.data) : null;

  const results = useMemo(() => searchQuick(query, { places, creators, creatorPages }), [query, places, creators, creatorPages]);

  const rows: { section: string | null; rows: Row[] }[] = useMemo(() => {
    if (error && !me) {
      return [{ section: null, rows: [{ key: "login", label: "Sign in to Signet", type: "Signet", icon: <SignetMark size={18} />, target: () => "/login" }] }];
    }

    const source = query.trim() ? "Instagram" : "Clipboard";
    const fromHint: Row[] = [];
    if (hint?.kind === "instagram") {
      const handle = hint.handle;
      if (can("research.account:create")) {
        fromHint.push({ key: `hint:${handle}`, label: <>Watch <strong>@{handle}</strong> in Research</>, type: source, icon: <InstagramIcon size={16} />, target: () => `/research/creators?add=1&handle=${encodeURIComponent(handle)}` });
      }
      if (can("research.account:view")) {
        fromHint.push({ key: `hint-find:${handle}`, label: <>Find <strong>@{handle}</strong> in Research</>, type: source, icon: <InstagramIcon size={16} />, target: () => `/research/creators?q=${encodeURIComponent(handle)}` });
      }
    } else if (hint?.kind === "url" && foundLink) {
      const shown = `${hint.host}${hint.path ? `/${hint.path}` : ""}`;
      fromHint.push({ key: `hint:${shown}`, label: <>Open link <strong>{shown}</strong></>, type: query.trim() ? "Link" : "Clipboard", icon: <LinkIcon size={16} />, target: () => `/links?edit=${encodeURIComponent(foundLink)}` });
    }

    if (query.trim()) return [{ section: null, rows: [...fromHint, ...results.map((r) => resultRow(r))] }];
    const fromClip = fromHint;
    const recentRows: Row[] = recents.map((r) => ({
      key: `recent:${r.to}`,
      label: r.label,
      type: r.kind === "creator" ? "Creator" : r.kind === "creator-page" ? "Creator page" : r.kind === "tab" ? "Tab" : "Page",
      icon: r.creator ? <CreatorAvatar name={r.creator} src={photoOf(r.creator)} size={20} /> : iconOf(r.to),
      target: () => r.to,
      recent: () => r,
      creator: r.creator,
    }));
    return [
      ...(fromClip.length ? [{ section: "From your clipboard", rows: fromClip }] : []),
      ...(recentRows.length ? [{ section: "Recent", rows: recentRows }] : []),
    ];

    function resultRow(r: QuickResult): Row {
      if (r.kind === "place") {
        return {
          key: r.id,
          label: r.place.label,
          detail: r.place.kind === "page" ? r.place.hint : undefined,
          type: r.place.kind === "tab" ? "Tab" : "Page",
          icon: iconOf(r.place.to),
          target: () => r.place.to,
          recent: (to) => ({ to, label: r.place.label, kind: r.place.kind }),
        };
      }
      if (r.kind === "creator") {
        const to = `/creators/${encodeURIComponent(r.creator.name)}`;
        return {
          key: r.id,
          label: r.creator.name,
          detail: r.creator.departed ? "Left" : undefined,
          type: "Creator",
          icon: <CreatorAvatar name={r.creator.name} src={r.creator.photo} size={20} />,
          target: () => to,
          recent: () => ({ to, label: r.creator.name, kind: "creator", creator: r.creator.name }),
          creator: r.creator.name,
        };
      }
      const page = creatorPages.find((p) => p.id === r.page.id)!;
      const label = `${r.creator.name} ${r.page.label}`;
      return {
        key: r.id,
        label: <>{r.creator.name} <span className="signet-qb-soft">{r.page.label}</span></>,
        type: "Creator page",
        icon: <CreatorAvatar name={r.creator.name} src={r.creator.photo} size={20} />,
        target: () => page.to(r.creator.name),
        recent: (to) => ({ to, label, kind: "creator-page", creator: r.creator.name }),
        creator: r.creator.name,
      };
    }
  }, [query, results, recents, hint, foundLink, creators, creatorPages, me, error]);

  const flat = rows.flatMap((s) => s.rows);
  // Typing picks the best match at once; with nothing typed, Enter brings
  // Signet forward unless a row was chosen with the arrows.
  useEffect(() => setActive(query.trim() && flat.length ? 0 : -1), [query, flat.length]);
  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  async function choose(row: Row | null) {
    if (opening) return;
    setOpening(true);
    try {
      const to = row ? await row.target() : null;
      if (row && to && row.recent) rememberRecent(row.recent(to));
      await openInSignet(to);
    } finally {
      setOpening(false);
    }
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => (flat.length ? Math.min(flat.length - 1, i + 1) : -1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => Math.max(query.trim() ? 0 : -1, i - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      void choose(active >= 0 ? (flat[active] ?? null) : null);
    } else if (event.key === "Escape") {
      event.preventDefault();
      if (query) setQuery("");
      else close();
    }
  }

  // The figures are Now's own cards, which link to their pages; here the
  // link opens in the main window rather than in this one.
  const onStatsClick = (event: React.MouseEvent) => {
    const anchor = (event.target as HTMLElement).closest("a");
    const href = anchor?.getAttribute("href");
    if (!href) return;
    event.preventDefault();
    event.stopPropagation();
    void openInSignet(href);
  };

  const current = active >= 0 ? flat[active] : null;
  let index = -1;

  // A creator's row opens her figures beside the list, in place of the
  // agency's across the top (creator-detail.tsx).
  const canClicks = can("links.analytics:view");
  const canRevenue = can("reporting.creator:view");
  const focus = current?.creator && (canClicks || canRevenue) ? current.creator : null;

  // Spotlight shows its field alone until there is something under it; so does this,
  // and the shell sizes the window to whatever is drawn.
  const hasBody = Boolean(me || error) && (rows.length > 0 || Boolean(focus) || (stats.length > 0 && !query.trim()) || Boolean(query.trim()));

  return (
    <div ref={panel} className={`signet-qb${hasBody ? " open" : ""}`} onMouseDown={(e) => e.target === e.currentTarget && input.current?.focus()}>
      <div className="signet-qb-search">
        <SearchIcon size={20} />
        <input
          ref={input}
          autoFocus
          spellCheck={false}
          autoComplete="off"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search Signet"
          aria-label="Search Signet"
          aria-controls="signet-qb-list"
          aria-activedescendant={current ? `signet-qb-row-${active}` : undefined}
          role="combobox"
          aria-expanded={hasBody}
        />
        {opening ? <span className="signet-qb-opening">Opening</span> : null}
      </div>

      {hasBody ? (
        <div className="signet-qb-content">
          {stats.length && !query.trim() && !focus ? (
            <div className="signet-qb-stats" onClickCapture={onStatsClick}>
              {stats.map((stat) => <stat.component key={stat.id} range={TODAY} />)}
            </div>
          ) : null}

          <div className={`signet-qb-body${focus ? " split" : ""}`}>
            <div className="signet-qb-list" id="signet-qb-list" role="listbox" ref={list} aria-label="Results">
              {rows.map((section) => (
                <div key={section.section ?? "results"} role="group" aria-label={section.section ?? "Results"}>
                  {section.section ? <div className="signet-qb-section">{section.section}</div> : null}
                  {section.rows.map((row) => {
                    index += 1;
                    const at = index;
                    return (
                      <div
                        key={row.key}
                        id={`signet-qb-row-${at}`}
                        data-index={at}
                        role="option"
                        aria-selected={at === active}
                        className={`signet-qb-row${at === active ? " on" : ""}`}
                        onMouseMove={() => at !== active && setActive(at)}
                        onClick={() => void choose(row)}
                      >
                        <span className="signet-qb-icon">{row.icon}</span>
                        <span className="signet-qb-label">{row.label}</span>
                        {row.detail ? <span className="signet-qb-detail">{row.detail}</span> : null}
                        {/* What Enter does, on the chosen row only, as Spotlight puts its hint. */}
                        <span className="signet-qb-type">{at === active ? <>Open <kbd>↵</kbd></> : row.type}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
              {query.trim() && flat.length === 0 && me ? <div className="signet-qb-empty">No results for "{query.trim()}"</div> : null}
              {!query.trim() && flat.length === 0 && me ? (
                <div className="signet-qb-empty"><ClockIcon size={14} /> Places and creators you open will show here</div>
              ) : null}
            </div>
            {focus ? <CreatorDetail name={focus} canClicks={canClicks} canRevenue={canRevenue} /> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function iconOf(to: string) {
  const Icon = placeIcon(to);
  return <Icon size={16} />;
}
